import { TabloCanvaComponent } from '../tablo-studio/tablo-canva/tablo-canva.component';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { ImageContainer } from '@ui/orders/graphql';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import {
  firstValueFrom,
  RxjsOperators,
} from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import {
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import {
  TabloProject,
  ZalamoObject,
} from '../../../../../../photographer-panel/src/graphql';
import {
  TabloElementGroupList,
  TabloStaticTextConfig,
  TabloTextConfig,
} from '@ui/tablo-generator/types';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import { FontLoaderService } from '@ui/fonts-display/font-loader.service';
import { GetImageUrlService } from 'ZalamoLibrary/src/lib/core/image/image-loader/get-image-url.service';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';

const filterHidden = (v: { hidden?: boolean; config?: { hidden?: boolean } }) =>
  !v.hidden && !v.config?.hidden;
@Component({
    selector: 'lib-print-canva',
    templateUrl: './print-canva.component.html',
    styleUrls: ['./print-canva.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PrintCanvaComponent extends AliveState implements OnInit {
  @ViewChild(TabloCanvaComponent) private renderCanvas?: TabloCanvaComponent;
  private preloadingForDiagnostics = true;

  getRenderDiagnostics(): Record<
    string,
    string | number | boolean | null | string[]
  > {
    return {
      preloading: this.preloadingForDiagnostics,
      diagnosticProjectId: this.projectId || null,
      ...(this.renderCanvas?.getRenderDiagnostics() || {}),
    };
  }

  currentPage$ = new Subject<number>();
  @Input() publicSessionOrganizationId: string;
  currentTemplate$ = new BehaviorSubject(null);
  @Input() set template(template) {
    this.preloadingForDiagnostics = true;
    this.currentTemplate$.next(template);
    if (template) {
      this.fullProject$.next(template);
    }
  }
  @Input() set page(page) {
    this.currentPage$.next(page);
  }
  projectStructureToPassDown$ = combineLatest([
    this.currentTemplate$,
    this.currentPage$,
  ]).pipe(
    map(([template, page]) => {
      if (!template) {
        return null;
      }
      return template.pages[page].structure.filter(filterHidden);
    }),
  );

  @Input() set photoSourceGroups(groups) {
    this.sourceGroups$.next(groups);
  }
  sourceGroups$ = new BehaviorSubject(null);
  @Input() @Required context: ImageContainer;
  @Input() @Required contextId: string;
  @Input() @Required publicSessionOrganizationGroupId: string;
  @Input() photographerLogo = '';
  @Output() readyToShot = new EventEmitter();
  @Input() projectId!: string;
  @Input() isMockup = false;
  fullProject$ = new BehaviorSubject<
    Omit<TabloProject, 'pages'> & { pages: any[] }
  >(null);
  leadingChildId$ = this.fullProject$.pipe(
    RxjsOperators.filterTruthy(),
    map(
      (struct) =>
        // @ts-ignore
        struct.pages
          .flatMap((v) => v.structure)
          .find((item) => item?.config?.config?.leadingChildId)?.config?.config
          ?.leadingChildId,
    ),
  );
  isPreloading$ = this.fullProject$.pipe(
    RxjsOperators.filterTruthy(),
    switchMap((project) =>
      this.organizationService.getCurrentOrganization().pipe(
        switchMap((organizationId) =>
          Promise.all([
            ...project.pages
              .filter((v) => v.url)
              .map(({ url }) =>
                this.getImageUrlService
                  .transform(
                    {
                      photoId: url,
                    },
                    `${this.projectId}-assets`,
                    'photographer',
                    ZalamoObject.DesignerAsset,
                    this.projectId,
                    5000,
                  )
                  .pipe(first())
                  .toPromise(),
              ),
            ...project.pages
              .flatMap((page) => page.structure)
              .filter(filterHidden)
              .filter(
                (v) =>
                  v.type === 'text' &&
                  v.config.config?.font?.fontId &&
                  v.config.config?.font?.fileName,
              )
              .map((v) =>
                this.fontLoaderService
                  .loadFont(
                    (v.config as TabloTextConfig).config.font?.fontId,
                    (v.config as TabloTextConfig).config.font?.fileName,
                    organizationId,
                  )
                  .pipe(first())
                  .toPromise(),
              ),
            ...project.pages
              .flatMap((page) => page.structure)
              .filter(filterHidden)
              .filter(
                (v) =>
                  (v.type === 'statictext' || v.type === 'group') &&
                  ((v.config.config?.font?.fontId &&
                    v.config.config?.font?.fileName) ||
                    (v.config.config?.fontOdd?.fontId &&
                      v.config.config?.fontOdd?.fileName) ||
                    (v.config.config?.fontEven?.fontId &&
                      v.config.config?.fontEven?.fileName) ||
                    (v.config.config?.headerFont?.fontId &&
                      v.config.config?.headerFont?.fileName)),
              )
              .flatMap((v) => {
                const fonts: {
                  config: {
                    config: { font: { fontId: string; fileName: string } };
                  };
                }[] = [];
                if (
                  (v.config as TabloStaticTextConfig).config.font?.fontId &&
                  (v.config as TabloStaticTextConfig).config.font?.fileName
                ) {
                  fonts.push({
                    config: {
                      config: {
                        font: {
                          fileName: (v.config as TabloStaticTextConfig).config
                            .font.fileName,
                          fontId: (v.config as TabloStaticTextConfig).config
                            .font.fontId,
                        },
                      },
                    },
                  });
                }
                if (
                  (v.config as TabloElementGroupList).config.font?.fontId &&
                  (v.config as TabloElementGroupList).config.font?.fileName
                ) {
                  fonts.push({
                    config: {
                      config: {
                        font: {
                          fileName: (v.config as TabloElementGroupList).config
                            .font.fileName,
                          fontId: (v.config as TabloElementGroupList).config
                            .font.fontId,
                        },
                      },
                    },
                  });
                }
                if (
                  (v.config as TabloElementGroupList).config.fontOdd?.fontId &&
                  (v.config as TabloElementGroupList).config.fontOdd?.fileName
                ) {
                  fonts.push({
                    config: {
                      config: {
                        font: {
                          fileName: (v.config as TabloElementGroupList).config
                            .fontOdd.fileName,
                          fontId: (v.config as TabloElementGroupList).config
                            .fontOdd.fontId,
                        },
                      },
                    },
                  });
                }
                if (
                  (v.config as TabloElementGroupList).config.fontEven?.fontId &&
                  (v.config as TabloElementGroupList).config.fontEven?.fileName
                ) {
                  fonts.push({
                    config: {
                      config: {
                        font: {
                          fileName: (v.config as TabloElementGroupList).config
                            .fontEven.fileName,
                          fontId: (v.config as TabloElementGroupList).config
                            .fontEven.fontId,
                        },
                      },
                    },
                  });
                }
                if (
                  (v.config as TabloStaticTextConfig).config.headerFont
                    ?.fontId &&
                  (v.config as TabloStaticTextConfig).config.headerFont
                    ?.fileName
                ) {
                  fonts.push({
                    config: {
                      config: {
                        font: {
                          fileName: (v.config as TabloStaticTextConfig).config
                            .headerFont.fileName,
                          fontId: (v.config as TabloStaticTextConfig).config
                            .headerFont.fontId,
                        },
                      },
                    },
                  });
                }
                console.log(fonts);
                return fonts;
              })
              .map((v) =>
                this.fontLoaderService
                  .loadFont(
                    (v.config as TabloTextConfig).config.font?.fontId,
                    (v.config as TabloTextConfig).config.font?.fileName,
                    organizationId,
                  )
                  .pipe(first())
                  .toPromise(),
              ),
          ]),
        ),
      ),
    ),
    map(() => false),
    startWith(true),
    tap((x) => {
      this.preloadingForDiagnostics = x;
    }),
    shareReplay(1),
  );
  constructor(
    private organizationService: OrganizationService,
    private fontLoaderService: FontLoaderService,
    private getImageUrlService: GetImageUrlService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(this.isPreloading$);
  }

  async canvasLoaded() {
    await firstValueFrom(this.isPreloading$);
    console.log('all loaded');
    this.readyToShot.emit();
  }
}
